/* Common Imports */

import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import useSWRInfinite from "swr/infinite";
import { useRouter } from "next/router";

/* Component Imports */

import { Typography, CircularProgress } from "@material-ui/core";
import GridCard from "./sub-components/GridCard";
import PostRequirementCard from "./sub-components/PostRequirementCard";

/* Function Imports */

import useOnScreen from "../../../lib/InfiniteScrollObserver";
import GetConfigIcon from "../../../lib/GetConfigIcon";

/* Define all styles */

const useStyles = makeStyles((theme) => ({
  cardOrganiser: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    margin:
      process.env.STICKY_SEARCHBAR_ON_PROPERTIES_PAGE === "true"
        ? "4rem 0rem 0rem 0rem"
        : "0rem 0rem 0rem 0rem",
    [theme.breakpoints.down("xs")]: {
      transition: "margin 500ms",
    },
  },
  loaderDiv: {
    width: "100%",
  },
  cardOrganiser2: {
    [theme.breakpoints.down("xs")]: {
      transition: "margin 500ms",
      margin:
        process.env.STICKY_SEARCHBAR_ON_PROPERTIES_PAGE === "true"
          ? "10.625rem 0rem 0rem 0rem"
          : "0rem 0rem 0rem 0rem",
    },
  },
}));

const InfiniteScroller = (props) => {
  const classes = useStyles();
  const router = useRouter();

  /* Infinite Scroller */
  const PAGE_SIZE = 3;
  const url = (index) => {
    return `https://lighteradvisory.com/api/properties/search?t=${
      router.query.t ? router.query.t : ""
    }&c=${router.query.c ? router.query.c : ""}&q=${
      router.query.q ? router.query.q : ""
    }&page=${index + 1}&valueMin=${
      router.query.valueMin ? router.query.valueMin : ""
    }&valueMax=${
      router.query.valueMax ? router.query.valueMax : ""
    }&budgetUnitMin=${
      router.query.budgetUnitMin ? router.query.budgetUnitMin : ""
    }&budgetUnitMax=${
      router.query.budgetUnitMax ? router.query.budgetUnitMax : ""
    }&property=${
      router.query.property ? router.query.property : ""
    }&specialtags=${
      router.query.specialtags ? router.query.specialtags : ""
    }&areaMin=${router.query.areaMin ? router.query.areaMin : ""}&areaMax=${
      router.query.areaMax ? router.query.areaMax : ""
    }&areaUnit=${router.query.areaUnit ? router.query.areaUnit : ""}&bhkMin=${
      router.query.bhkMin ? router.query.bhkMin : ""
    }&bhkMax=${router.query.bhkMax ? router.query.bhkMax : ""}&floorsMin=${
      router.query.floorsMin ? router.query.floorsMin : ""
    }&floorsMax=${
      router.query.floorsMax ? router.query.floorsMax : ""
    }&bathroomMin=${
      router.query.bathroomMin ? router.query.bathroomMin : ""
    }&bathroomMax=${router.query.bathroomMax ? router.query.bathroomMax : ""}`;
  };

  const fetcher = (url) =>
    fetch(url, { cache: "no-store" }).then((res) => res.json());

  const { data, error, size, setSize } = useSWRInfinite(url, fetcher);
  const properties = data ? [].concat(...data) : [];
  const isLoadingInitialData = !data && !error;
  const isLoadingMore =
    isLoadingInitialData ||
    (size > 0 && data && typeof data[size - 1] === "undefined");
  const isEmpty = data?.[0]?.length === 0;
  const isReachingEnd =
    isEmpty || (data && data[data.length - 1]?.length < PAGE_SIZE);

  const loadMore = React.useRef(null);

  const isOnScreen = useOnScreen(loadMore, "100px");

  React.useEffect(() => {
    if (isOnScreen) {
      setSize(size + 1);
    } else {
    }
    if (isReachingEnd) {
      props.iocb(false);
    } else {
      props.iocb(true);
    }
    if (properties && properties.length > 0) {
      props.numResults(properties[0].searchResultCount);
    }
  }, [isOnScreen, isReachingEnd]);

  return (
    <React.Fragment>
      <div
        className={`${classes.cardOrganiser} ${
          !props.scrollTrigger ? classes.cardOrganiser2 : undefined
        }`}
      >
        {properties.map((property, key) => {
          if (key !== 0 && key % 10 === 0) {
            return (
              <React.Fragment key={key}>
                <PostRequirementCard />
                <GridCard
                  slug={property.slug}
                  imageSrc={property.thumbnail_image.url}
                  title={property.title}
                  location={property.location.title}
                  builder={property.builder.name}
                  price={property.price}
                  area={property.area}
                  areaType="carpet_area"
                  areaUnit="ft"
                  unitIcon={GetConfigIcon(property.tags)}
                  config={property.config}
                  bhk={property.bhk}
                  chips={property.tags}
                  pdfHref={property.pdf}
                  loading={isLoadingInitialData || isLoadingMore}
                />
              </React.Fragment>
            );
          } else {
            return (
              <GridCard
                key={key}
                slug={property.slug}
                imageSrc={property.thumbnail_image.url}
                title={property.title}
                location={property.location.title}
                builder={property.builder.name}
                price={property.price}
                area={property.area}
                areaType="carpet_area"
                areaUnit="ft"
                unitIcon={GetConfigIcon(property.tags)}
                config={property.config}
                bhk={property.bhk}
                chips={property.tags}
                pdfHref={property.pdf}
                pdf={property.pdf}
                loading={isLoadingInitialData || isLoadingMore}
              />
            );
          }
        })}
      </div>
      <div ref={loadMore} className={classes.loaderDiv}>
        {isLoadingMore ? (
          ""
        ) : isReachingEnd ? (
          <center>
            <Typography variant="body1" component="p">
              That&apos;s all for now. Keep coming back for new properties!
            </Typography>
          </center>
        ) : (
          <center>
            <CircularProgress />
          </center>
        )}
      </div>
    </React.Fragment>
  );
};

export default InfiniteScroller;
